package de.oevbs.kunde.kundenportal2.services.authentication.store

import de.oevbs.kunde.kundenportal2.PinAuthenticationStep
import de.oevbs.kunde.kundenportal2.PortalMessages
import de.oevbs.kunde.kundenportal2.authentication.PortalSession
import de.oevbs.kunde.kundenportal2.authentication.PortalSessionMiddleware
import de.oevbs.kunde.kundenportal2.components.GlobalServiceMessagesStore
import de.oevbs.kunde.kundenportal2.components.globalSubmitTracker
import de.oevbs.kunde.kundenportal2.model.services.authentication.PinAuthentication
import de.oevbs.kunde.kundenportal2.services.authentication.PartnerAuthenticationClient
import de.oevbs.kunde.kundenportal2.util.tryCatchAndHandleErrors
import de.oevbs.sfa.communication.use
import dev.fritz2.core.Handler
import dev.fritz2.core.invoke
import dev.fritz2.core.storeOf
import dev.fritz2.headless.validation.ComponentValidationMessage
import dev.fritz2.validation.ValidatingStore
import dev.fritz2.validation.valid
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow

class PinAuthenticationStore(job: Job) :
    ValidatingStore<PinAuthentication, PinAuthenticationStep, ComponentValidationMessage>(
        initialData = PinAuthentication(),
        validation = PinAuthentication.validation,
        metadataDefault = PinAuthenticationStep.CHECKPIN1,
        validateAfterUpdate = false,
        job = job
    ) {

    private val client = PartnerAuthenticationClient().use(PortalSessionMiddleware)

    private val progressStore = storeOf(PinAuthenticationStep.CHECKPIN1, job)
    val currentStep: Flow<PinAuthenticationStep> = progressStore.data

    private val headerText = storeOf("Freischaltcode für Vertragsverknüpfung eingeben", job)
    val header: Flow<String> = headerText.data

    val submitPin: Handler<Unit> = handle { pinAuthentication ->

        GlobalServiceMessagesStore.clear()

        if (!validate(pinAuthentication, PinAuthenticationStep.CHECKPIN1).valid) {
            return@handle pinAuthentication
        }
        val returnedAuth = globalSubmitTracker.track {
            checkPin(pinAuthentication)
        } ?: return@handle pinAuthentication

        // PIN ist korrekt, aber der User hat unterschiedliche Mailadressen in Account und CRM --> Auswahl anzeigen
        if (returnedAuth.crmMail.isNotEmpty()) {
            progressStore.update(PinAuthenticationStep.SELECTMAIL2)
        } else refreshAuthentication()
        returnedAuth
    }

    val requestNewPin: Handler<Unit> = handle {

        GlobalServiceMessagesStore.clear()

        val success = globalSubmitTracker.track {
            requestNewPin()
        }

        if (success) GlobalServiceMessagesStore.add(PortalMessages.pinLetterNewInfo)

        PinAuthentication()
    }

    val submitMailSelect: Handler<Unit> = handle { pinAuthentication ->
        GlobalServiceMessagesStore.clear()

        if (!validate(pinAuthentication, PinAuthenticationStep.SELECTMAIL2).valid) {
            return@handle pinAuthentication
        }

        val mailSaved = globalSubmitTracker.track {
            saveMailAddress(pinAuthentication)
        }

        if (mailSaved) {
            refreshAuthentication()
            progressStore.update(PinAuthenticationStep.SUCCESS3)
        } else return@handle pinAuthentication

        PinAuthentication()
    }

    private val refreshAuthentication: Handler<Unit> = handle {
        // Refresh PortalSession um die neue Rolle am User zu haben.
        // Muss in dediziertem Handler ausgeführt werden.
        it.also { PortalSession.refreshJwt() }
    }

    private suspend fun checkPin(pinAuthentication: PinAuthentication): PinAuthentication? {
        return tryCatchAndHandleErrors("Prüfen der Auth-PIN") {
            client.pinAuthenticate(pinAuthentication)
        }
    }

    private suspend fun requestNewPin(): Boolean {
        tryCatchAndHandleErrors("Anfordern eines neuen PIN-Briefs") {
            client.sendNewPinLetter()
        } ?: return false

        return true
    }

    private suspend fun saveMailAddress(pinAuthentication: PinAuthentication): Boolean {
        tryCatchAndHandleErrors("Schritt 2 der PinAuthentifizierung (Mail ändern)") {
            client.changeMail(pinAuthentication)
        } ?: return false

        return true
    }
}